import type { Assignee, FollowUpPriority, KeyedLabel, VisitRow } from '@fitting-room/contracts'
import { asksWhyNoPurchase } from '@fitting-room/contracts/appointment-status'
import { boutiqueDayOf } from './boutique'

/**
 * The words and the arithmetic the appointment screens share, kept out of the
 * components so they can be tested in `node` like the rest of `lib/`.
 */

type VisitStatus = VisitRow['status']

export const VISIT_STATUS_LABEL: Record<VisitStatus, string> = {
  booked: 'Booked',
  completed: 'Completed',
  no_show: 'No-show',
  cancelled: 'Canceled',
}

/** The order the status chips are drawn in: the day as it unfolds. */
export const VISIT_STATUS_ORDER: readonly VisitStatus[] = ['booked', 'completed', 'no_show', 'cancelled']

/**
 * What a button that MOVES an appointment to a status says.
 *
 * The way back to booked says what it undoes — "Undo canceled" — because a
 * bare "Mark booked" beside a status that reads "Canceled" sounds like a
 * second booking rather than a correction (tperent's "復原完成" for the same
 * reason).
 */
export function statusActionLabel(from: VisitStatus, to: VisitStatus): string {
  if (to === 'booked') return `Undo ${VISIT_STATUS_LABEL[from].toLowerCase()}`
  if (to === 'completed') return 'Mark completed'
  if (to === 'no_show') return 'Mark no-show'
  return 'Cancel appointment'
}

/**
 * Whether the drawer offers a follow-up: when the customer came and left
 * without an order (`asksWhyNoPurchase` — a customer who bought is followed
 * by the order, and one not seen yet has nothing to follow up on), or
 * whenever one is already set, so nothing about the appointment changing
 * ever hides a date somebody still has to act on.
 *
 * There is no "Sales status" to read any more: the owner decided on
 * 2026-10-15 that whether the appointment has an order is the answer.
 */
export function offersFollowUp(visit: Pick<VisitRow, 'status' | 'orders' | 'followUpOn'>, pickup = false): boolean {
  return asksWhyNoPurchase(visit, pickup) || visit.followUpOn !== undefined
}

/**
 * The types a picker offers: those the owner still offers, and the one the
 * appointment already has, whatever it is — keeping it is not booking it.
 */
export function offeredTypes<T extends { readonly key: string; readonly retired: boolean }>(types: readonly T[], current?: string): T[] {
  return types.filter((type) => !type.retired || type.key === current)
}

export const FOLLOW_UP_PRIORITY_LABEL: Record<FollowUpPriority, string> = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
}

/**
 * A stored key, in the words the owner gave it — or the key itself.
 *
 * The owner can rename or remove an entry at any time, and appointments that
 * chose it keep the key. A renamed one reads with its new label; a removed
 * one has no label left, and the honest thing to show is what was stored.
 */
export function labelForKey(list: readonly KeyedLabel[] | undefined, key: string | undefined): string | undefined {
  if (!key) return undefined
  return list?.find((entry) => entry.key === key)?.label ?? key
}

/** A pick list as a `<select>` offers it: the current entries, plus the one stored if it has since left the list. */
export function optionsWithStored(list: readonly KeyedLabel[] | undefined, stored: string | undefined): KeyedLabel[] {
  const options = [...(list ?? [])]
  if (stored && !options.some((entry) => entry.key === stored)) options.push({ key: stored, label: stored })
  return options
}

/**
 * The appointments page, opened on the day of this one with its drawer open:
 * where an order's appointment links to, and where a new order written from
 * an appointment goes back to when it is canceled.
 */
export function appointmentHref(visit: Pick<VisitRow, 'id' | 'scheduledAt'>): string {
  const day = boutiqueDayOf(visit.scheduledAt)
  return `/admin?visit=${encodeURIComponent(visit.id)}&from=${day}&to=${day}`
}

/** Assignee id → their calendar mark. Members with none are absent, so their appointments show no mark. */
export function markByAssignee(members: readonly Assignee[] | undefined): Map<string, string> {
  const map = new Map<string, string>()
  for (const member of members ?? []) if (member.calendarMark) map.set(member.id, member.calendarMark)
  return map
}

/**
 * Dollars as a person types them, in minor units: "1,250" and "$1250.50"
 * both read. `null` for a blank field, `undefined` for something that is not
 * an amount, which the form refuses before sending.
 */
export function minorFromDollars(raw: string): number | null | undefined {
  const text = raw.trim().replace(/^\$/, '').replace(/,/g, '').trim()
  if (text === '') return null
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return undefined
  return Math.round(Number(text) * 100)
}

/** 125000 → "1250"; 125050 → "1250.50". For a field, so no symbol and no grouping. */
export function dollarsFromMinor(minor: number | undefined): string {
  if (minor === undefined) return ''
  return minor % 100 === 0 ? String(minor / 100) : (minor / 100).toFixed(2)
}

/** 125000 → "$1,250". */
export function formatUsd(minor: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100)
}

/** "13:30" plus 90 minutes → "15:00", held at the end of the day rather than wrapping into the next. */
export function addMinutesToWallTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number)
  const total = Math.min((h ?? 0) * 60 + (m ?? 0) + minutes, 23 * 60 + 59)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** "13:30" → "1:30 PM", the way the rest of the console prints a time. */
export function formatWallTime(time: string): string {
  const [h, m] = time.split(':').map(Number)
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return time
  const suffix = h < 12 ? 'AM' : 'PM'
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`
}

/** A duration as a stylist says it: "45 min", "1 hr", "1 hr 30 min". */
export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m} min`
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`
}

/** Their name, for a screen; a mark is meaningless read aloud and is never the only label. */
export function assigneeName(members: readonly Assignee[] | undefined, id: string | undefined): string | undefined {
  if (!id) return undefined
  return members?.find((member) => member.id === id)?.displayName ?? 'A former team member'
}
